import { useState } from 'react'

import {
  Box,
  Button,
  Chevron,
  Text,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import i18n from 'modules/core/lib/i18n'

import AddQuestionModalContainer from 'modules/reviews/components/Questions/AddQuestion/AddQuestionModalContainer'
import EditCategoryModalContainer from 'modules/reviews/components/Questions/Categories/EditCategoryModalContainer'
import RemoveCategoryModalContainer from 'modules/reviews/components/Questions/Categories/RemoveCategoryModalContainer'
import type QuestionCategory from 'modules/reviews/store/models/QuestionCategory'
import type QuestionGroupConfig from 'modules/reviews/store/models/QuestionGroupConfig'
import type Review from 'modules/reviews/store/models/v2/Review'

interface Props {
  form: Review
  questionGroupConfig: QuestionGroupConfig
  questionCategory: QuestionCategory
  canEdit: boolean
  expanded: boolean
}

function CategoryHeader({ form, questionCategory, questionGroupConfig, canEdit, expanded }: Props) {
  const [isTooltipOpen, toggleTooltipOpen] = useToggle(false)
  const isEditable = !form.isActive && !form.isFinished
  const [showEditSectionModal, setShowEditSectionModal] = useState(false)
  const [showRemoveSectionModal, setShowRemoveSectionModal] = useState(false)
  const [showAddQuestionToSectionModal, setShowAddQuestionToSectionModal] = useState(false)

  const editAction = {
    onClick: () => setShowEditSectionModal(true),
    title: i18n.t('surveys.form.question_categories.edit_button'),
  }

  const deleteAction = {
    onClick: () => {
      setShowRemoveSectionModal(true)
    },
    danger: true,
    title: i18n.t('surveys.form.question_categories.delete_button'),
  }

  const actions = (() => [editAction, deleteAction])()

  return (
    <>
      <Box
        flexDirection="row"
        paddingLeft="s32"
        paddingX="s16"
        borderRadius={{
          all: 'abs004',
        }}
        border={{
          all: {
            style: 'solid',
            width: 's1',
            color: 'grey400',
          },
        }}
        flexGrow
        cursor="pointer"
        background="grey200"
        expanded={expanded}
      >
        <Box justifyContent="center" paddingX="s2">
          <Chevron direction={expanded ? 'down' : 'right'} type="secondary" />
        </Box>
        <Box paddingY="s8" paddingX="s12" gap="s2" flexGrow>
          <Text.Normal color="grey1000" weight="medium" size="300">
            {questionCategory.attributes.name}
          </Text.Normal>
          <Text.Normal color="grey800" weight="medium" size="200">
            {i18n.tp('surveys.form.question_categories.question_configs_count', {
              count: questionCategory.questionConfigs.length,
            })}
          </Text.Normal>
        </Box>
        {canEdit && (
          <Box
            alignItems="center"
            flexDirection="row"
            gap="s8"
            onClick={(ev: MouseEvent) => ev.stopPropagation()}
          >
            <Tooltip.Alt
              attachment="top center"
              offset="-10px 0"
              open={isTooltipOpen}
              toggle={toggleTooltipOpen}
              label={i18n.t('surveys.form.question_categories.add_question')}
            >
              <Button
                type="secondary"
                onClick={() => {
                  setShowAddQuestionToSectionModal(true)
                }}
                iconName="add"
              />
            </Tooltip.Alt>
            {isEditable && (
              <PopoverMenu type="list">
                <PopoverMenu.Trigger>
                  <Button.Decorative type="secondary" iconName="ellipsis" />
                </PopoverMenu.Trigger>
                <PopoverMenu.Content side="bottom" align="center">
                  {actions.map((action) => (
                    <PopoverMenu.TouchableOption key={action.title} {...action} />
                  ))}
                </PopoverMenu.Content>
              </PopoverMenu>
            )}

            {showEditSectionModal && (
              <EditCategoryModalContainer
                formId={form.id}
                onClose={() => {
                  setShowEditSectionModal(false)
                }}
                questionCategoryId={questionCategory.attributes.id}
              />
            )}

            {showRemoveSectionModal && (
              <RemoveCategoryModalContainer
                formId={form.id}
                onClose={() => {
                  setShowRemoveSectionModal(false)
                }}
                questionCategoryId={questionCategory.attributes.id}
              />
            )}

            {showAddQuestionToSectionModal && (
              <AddQuestionModalContainer
                reviewId={form.id}
                onClose={() => {
                  setShowAddQuestionToSectionModal(false)
                }}
                questionGroupConfigId={questionGroupConfig.attributes.id}
                questionCategoryId={questionCategory.attributes.id}
              />
            )}
          </Box>
        )}
      </Box>
    </>
  )
}

export default observer(CategoryHeader)
